/**
 * Shared subscription state for the authenticated app.
 *
 * A context rather than a plain hook because four separate places need this
 * (usage banner, Settings billing card, the connect-page gate, and the
 * moderation paused notice). A per-mount hook would fire four requests on every
 * route change.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useApi, API_ENDPOINTS } from "../lib/api";
import { Subscription } from "../types/subscription";

interface SubscriptionContextValue {
  subscription: Subscription | null;
  loading: boolean;
  refresh: () => Promise<void>;
  /** False only when we positively know the user is at their page limit. */
  canConnectPage: boolean;
  commentPercent: number;
  pagePercent: number;
}

// Default value applies only outside a provider (e.g. public routes), where
// there is no subscription to refresh.
const SubscriptionContext = createContext<SubscriptionContextValue>({
  subscription: null,
  loading: true,
  refresh: async () => undefined,
  canConnectPage: true,
  commentPercent: 0,
  pagePercent: 0,
});

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const api = useApi();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get(API_ENDPOINTS.SUBSCRIPTION);
      if (res.data?.subscription) setSubscription(res.data.subscription);
    } catch (err) {
      // Non-fatal by design: a billing outage must not blank the app.
      console.error("[useSubscription] fetch failed", err);
    } finally {
      setLoading(false);
    }
    // `api` is intentionally not a dependency: useApi() returns a fresh instance
    // each render, so depending on it would re-create refresh every render and
    // re-trigger the effect below in a loop.
  }, []); // eslint-disable-line

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo<SubscriptionContextValue>(() => {
    const pages = subscription?.usage.pages;
    const comments = subscription?.usage.comments;

    // Default to ALLOWING the connect when we have no data (loading or API
    // error). The backend enforces the real limit at OAuth time; a billing blip
    // must never block onboarding.
    const canConnectPage =
      !subscription || pages?.limit == null ? true : pages.used < pages.limit;

    return {
      subscription,
      loading,
      refresh,
      canConnectPage,
      commentPercent: comments?.percent ?? 0,
      pagePercent: pages?.percent ?? 0,
    };
  }, [subscription, loading, refresh]);

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};

export function useSubscription(): SubscriptionContextValue {
  return useContext(SubscriptionContext);
}
